import { QRCodeSVG } from "qrcode.react";
import { Badge, Title, Text } from "rizzui";
import Table, { HeaderCell } from "@/components/legacy-table";
import { siteConfig } from "@/config/site.config";
import { Billing, Order, OrderStatus } from "@/types";
import { formatDate } from "@/utils/format-date";
import { fetchOrderById } from "@/server/api-services";
import { useState, useEffect } from "react";

export const getColumns = () => [
    {
        title: <span className="ms-6 block whitespace-nowrap">Date</span>,
        dataIndex: "createdDate",
        key: "createdDate",
        width: 200,
        render: (createdDate: string) => (
            <span className="ms-6 block">
                <Text className="mb-1 font-medium text-gray-700">
                    {formatDate(new Date(createdDate), "MMMM D, YYYY")}
                </Text>
                <Text className="text-[13px] text-gray-500">
                    {formatDate(new Date(createdDate), "h:mm A")}
                </Text>
            </span>
        ),
    },
    {
        title: <HeaderCell title="Reason" />,
        dataIndex: "billingType",
        key: "billingType",
        width: 200,
        render: (billingType: string) => <Text>{billingType}</Text>,
    },
    {
        title: <HeaderCell title="Billed By" />,
        dataIndex: "client",
        key: "client",
        width: 200,
        render: (createdUser: string) => <Text>{createdUser}</Text>,
    },
    {
        title: <HeaderCell title="Remarks" />,
        dataIndex: "remarks",
        key: "remarks",
        width: 200,
        render: (remarks: string) => <Text>{remarks}</Text>,
    },
    {
        title: <HeaderCell title="Type" />,
        dataIndex: "debit",
        key: "debit",
        width: 200,
        render: (debit: number) => (
            <Badge variant="flat" color="success" size="sm">
                {debit == 0 ? "Payment" : "Invoice"}
            </Badge>
        ),
    },
    {
        title: <HeaderCell title="Invoiced" />,
        dataIndex: "debit",
        key: "debit",
        width: 200,
        render: (debit: number) => <Text>${debit}</Text>,
    },
    {
        title: <HeaderCell title="Payment" />,
        dataIndex: "credit",
        key: "credit",
        width: 200,
        render: (debit: number) => <Text>${debit}</Text>,
    },
];

function BillingDetailsListTable({ Billings }: { Billings: Billing[] }) {
    return (
        <Table
            data={Billings}
            columns={getColumns()}
            variant="minimal"
            rowKey={(record: any) => record.id}
            scroll={{ x: 660 }}
            className="mb-11"
        />
    );
}

type BillingDetailsProps = {
    Billings: Billing[];
    order: Order;
};

export default function BillingDetails({
    order,
    Billings,
}: BillingDetailsProps) {
    var totalAmountCredit = Billings.reduce(
        (acc, curr) => acc + curr.credit,
        0,
    );

    var totalDebitAmount = Billings.reduce((acc, curr) => acc + curr.debit, 0);
    return (
        <div className="w-full rounded-xl border border-muted p-5 text-sm sm:p-6 lg:p-8 2xl:p-10">
            <div className="mb-12 flex flex-col-reverse items-start justify-between md:mb-16 md:flex-row">
                <img
                    src={siteConfig.logo ?? ""}
                    alt={siteConfig.title}
                    className="dark:invert"
                    width={80}
                    height={80}
                />
                <div className="mb-4 md:mb-0">
                    <Badge
                        variant="flat"
                        color="success"
                        rounded="md"
                        className="mb-3 md:mb-2"
                    >
                        {order?.status}
                    </Badge>
                    <Title as="h6">INV - #{order?.orderId}</Title>
                    <Text className="mt-0.5 text-gray-500">Billing Number</Text>
                </div>
            </div>

            <div className="mb-12 grid gap-4 xs:grid-cols-2 sm:grid-cols-3 sm:grid-rows-1">
                <div className="">
                    <Title as="h6" className="mb-3.5 font-semibold">
                        From
                    </Title>
                    <Text className="mb-1.5 text-sm font-semibold uppercase">
                        Afro Cargo
                    </Text>
                    <Text className="mb-1.5">Jerome Bell</Text>
                    <Text className="mb-1.5">
                        4140 Rard Rd. Saka, <br /> China 31134
                    </Text>
                    <Text className="mb-4 sm:mb-6 md:mb-8">(+08) 555-0107</Text>
                    <div>
                        <Text className="mb-2 text-sm font-semibold">
                            Printed Date
                        </Text>
                        <Text>{new Date().toDateString()}</Text>
                    </div>
                </div>

                <div className="mt-4 xs:mt-0">
                    <Title as="h6" className="mb-3.5 font-semibold">
                        Billingd To
                    </Title>
                    <Text className="mb-1.5 text-sm font-semibold uppercase">
                        {order?.client?.fullname}
                    </Text>
                    <Text className="mb-1.5">{order?.client?.email}</Text>
                    <Text className="mb-1.5">{order?.shippingAddress}</Text>
                    <Text className="mb-4 sm:mb-6 md:mb-8">
                        +{order?.client?.phoneNumber}
                    </Text>
                    <div>
                        <Text className="mb-2 text-sm font-semibold">
                            Due Date
                        </Text>

                        <Text>
                            {new Date(
                                new Date().setDate(new Date().getDate() + 30),
                            ).toDateString()}
                        </Text>
                    </div>
                </div>

                <div className="mt-4 flex sm:mt-6 md:mt-0 md:justify-end">
                    <QRCodeSVG
                        value={"http://localhost:5172/orders/" + order?.id}
                        className="h-28 w-28 lg:h-32 lg:w-32"
                    />
                </div>
            </div>

            <BillingDetailsListTable Billings={Billings} />

            <div className="flex flex-col-reverse items-start justify-between border-t border-muted pb-4 pt-8 xs:flex-row">
                <div className="mt-6 max-w-md pe-4 xs:mt-0">
                    <Title
                        as="h6"
                        className="mb-1 text-xs font-semibold uppercase xs:mb-2 xs:text-sm"
                    >
                        Notes
                    </Title>
                    <Text className="leading-[1.7]">
                        Please make sure to pay the Billing before the due date.
                        If you have any questions, please contact us at{" "}
                        <a
                            href="mailto:support@afroCargo.com"
                            className="text-primary"
                        >
                            support@afroCargo.com
                        </a>
                    </Text>
                </div>
                <div className="w-full max-w-sm">
                    <Text className="flex items-center justify-between border-b border-muted py-3.5 lg:py-5">
                        Payments:{" "}
                        <Text as="span" className="font-semibold">
                            ${totalAmountCredit}
                        </Text>
                    </Text>
                    <Text className="flex items-center justify-between border-b border-muted py-3.5 lg:py-5">
                        Invoices:{" "}
                        <Text as="span" className="font-semibold">
                            ${totalDebitAmount}
                        </Text>
                    </Text>
                    <Text className="flex items-center justify-between border-b border-muted py-3.5 lg:py-5">
                        Taxes:
                        <Text as="span" className="font-semibold">
                            0
                        </Text>
                    </Text>
                    <Text className="flex items-center justify-between pt-4 text-base font-semibold text-gray-900 lg:pt-5">
                        Total Amount to be Paid:{" "}
                        <Text as="span">
                            {totalAmountCredit < totalDebitAmount ? (
                                <span className="text-md font-bold text-red-500">
                                    ${totalDebitAmount - totalAmountCredit}
                                </span>
                            ) : (
                                <span className="text-md font-bold text-green-500">
                                    ${totalAmountCredit - totalDebitAmount}
                                </span>
                            )}
                        </Text>
                    </Text>
                </div>
            </div>
        </div>
    );
}
